/**
 * useConversationChat Hook
 * API hooks for conversation chat operations with streaming support
 *
 * Refactored: Phase 4 - Factory Pattern Migration
 * Pattern: Uses createMutationHook factory for standardized mutations
 * Note: useSendMessage uses custom implementation for SSE streaming (cannot use factory)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiFetchUrl } from '../../config/apiConfig';
import { conversationKeys } from './useConversations';
import { log } from '../../utils/errors/logger';
import { ChatRequest, ToolStep } from '../../api/data-contracts';
import { ChatSource } from '../../api/chatTypes';
import { getCsrfToken, requiresCsrfToken } from '../../utils/auth/csrfManager';
import { parseSseFrames, assertBufferWithinLimit, type SseEvent } from './sseFrameParser';
import { applyToolStepEvent } from './toolStepAccumulator';

// --- Types ---

// Re-exported for consumers that want to type-check against the SSE event
// union directly (the union itself is hand-maintained in sseFrameParser.ts,
// not generated -- backend payload shapes come from
// backend/api/services/rag/citation_policy.py build_gated_stream_events
// and the streaming chat router).
export type { SseEvent };

interface SendMessageVariables {
  conversationId: string;
  content: string;
  /**
   * Called for each content chunk and whenever sources become available.
   * - `chunk`: new text content (empty string when only sources updated).
   * - `sources`: ChatSource array (from `context` / `sources_filtered` events).
   * - `sourcesFinal`: true only for the final cited-only `sources_filtered`
   *   event. The upfront `context` event is a wide "searching these documents"
   *   preview; consumers should resolve inline citation chips against it but may
   *   defer rendering the source panel until the final (cited-only) list to
   *   avoid showing the misleading full candidate set during generation.
   *
   * Streaming is the only supported mode -- onChunk is required. A citation-
   * gated turn still flows through here as ordinary content (see the `gated`
   * event handling in the mutation body); onChunk does not need to special-case it.
   * - `toolSteps`: the in-flight turn's accumulated MCP tool activity (see
   *   `applyToolStepEvent`), passed whenever a `tool_call`/`tool_result`/
   *   `tool_steps_discarded` event updates it. `undefined` means "no change";
   *   consumers that don't care about live tool activity can ignore it.
   */
  onChunk: (
    chunk: string,
    sources?: ChatSource[],
    sourcesFinal?: boolean,
    toolSteps?: ToolStep[],
    finalContent?: string,
  ) => void;
}

interface SendMessageResponse {
  message: { role: 'assistant'; content: string };
  context: ChatSource[];
  id: string;
  created: number;
}

// --- Custom Streaming Hook (Cannot use factory due to SSE) ---

/**
 * Hook to send a message in a conversation with streaming support
 *
 * Note: This hook uses custom implementation because it handles Server-Sent Events (SSE)
 * streaming which requires direct fetch API access and custom response handling.
 * The factory pattern is not suitable for this use case.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId, content, onChunk }: SendMessageVariables): Promise<SendMessageResponse> => {      // Headers for streaming request
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // SECURITY: Add CSRF token for POST request (VULN-001 FIX)
      const csrfToken = getCsrfToken();
      if (csrfToken && requiresCsrfToken('POST')) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const chatRequest: ChatRequest = {
        messages: [{ role: 'user', content }],
        stream: true, // Enable streaming
      };

      log.api.request('POST', `/api/conversations/${conversationId}/chat`, {
        conversationId,
        contentLength: content.length,
        streaming: true
      });

      const response = await fetch(`${getApiFetchUrl()}/conversations/${conversationId}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(chatRequest),
        credentials: 'include', // Send cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Streaming response body is not available');
      }

      // Handle streaming response. The reader loop applies each parsed SSE
      // event to `fullContent`/`context`; frame parsing itself (chunk
      // reassembly, JSON parsing, error-event detection) lives in the pure,
      // unit-tested `parseSseFrames` helper.
      let fullContent = '';
      let context: ChatSource[] = [];
      let toolSteps: ToolStep[] = [];

      const applyEvent = (event: SseEvent): boolean => {
        // Tool-step bookkeeping runs for every event up front (most events
        // are a no-op here, in which case applyToolStepEvent returns the
        // same array reference, so this never triggers a spurious onChunk
        // call for e.g. a plain `content` chunk).
        const nextToolSteps = applyToolStepEvent(toolSteps, event);
        if (nextToolSteps !== toolSteps) {
          toolSteps = nextToolSteps;
          onChunk('', undefined, false, toolSteps);
        }

        switch (event.type) {
          case 'context':
            // Upfront preview: the full candidate set ("searching these
            // documents"). Kept for inline-chip resolution during the
            // stream, but flagged NOT final so consumers can defer
            // rendering the source panel until the cited-only list.
            context = event.context ?? [];
            onChunk('', context, false);
            return false;
          case 'sources_filtered':
            // Citation-gating active: the final filtered (cited-only)
            // source subset. This is the result to display.
            context = event.context ?? [];
            onChunk('', context, true);
            return false;
          case 'gated':
            // Signals that citation-gating blocked generation for this turn
            // (no usable retrieved context). The `content` event that follows
            // still carries the clarifying-question answer text, so there is
            // no dedicated UI treatment needed here beyond observability.
            log.info('Citation-gated turn', { message: event.message });
            return false;
          case 'content':
            fullContent += event.content;
            onChunk(event.content, context, false);
            return false;
          case 'answer_final':
            // The server post-processed the answer (citation markers
            // normalised or completed); replace the streamed text with it.
            fullContent = event.content;
            onChunk('', context, false, undefined, event.content);
            return false;
          case 'done':
            return true;
          default:
            return false;
        }
      };

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Cancel the underlying body stream before releasing the reader lock
      // on an early exit (a stream `error` event, or an unexpected throw
      // from `applyEvent`) so the connection is torn down instead of left
      // half-read. Guarded because `cancel()` can itself reject if the
      // stream is already closed/errored -- that's not actionable here.
      const cancelReader = (): void => {
        void reader.cancel().catch(() => {
          // Stream already closed/errored -- nothing to do.
        });
      };

      try {
        let streamDone = false;
        while (!streamDone) {
          const { done, value } = await reader.read();

          if (done) {
            // Flush the decoder itself (no args): a multi-byte UTF-8
            // sequence split across the last two `read()` chunks would
            // otherwise have its trailing bytes silently dropped instead of
            // decoded into the buffer.
            buffer += decoder.decode();
            // Flush: parse whatever is left in the buffer as a final frame
            // rather than silently dropping it (the stream may end without a
            // trailing frame boundary).
            const { events, error } = parseSseFrames(buffer, { flush: true });
            // Apply events collected before the error (if any) first, so
            // content coalesced into the same buffer as a trailing
            // SAVE_FAILED/QUOTA error frame still reaches the UI.
            for (const event of events) {
              if (applyEvent(event)) {
                streamDone = true;
                break;
              }
            }
            if (error) {
              throw error;
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const { events, remainder, error } = parseSseFrames(buffer);
          buffer = remainder;
          // Guard against unbounded memory growth if the server streams a
          // malformed or never-terminated frame (no `\n\n` boundary ever
          // arrives): without this, `buffer` would keep growing on every
          // subsequent chunk for the rest of the connection's lifetime.
          assertBufferWithinLimit(buffer);

          for (const event of events) {
            if (applyEvent(event)) {
              streamDone = true;
              break;
            }
          }

          if (error) {
            // Same ordering: events already applied above, now propagate.
            throw error;
          }
        }
      } catch (streamError) {
        // Early exit (stream `error` event or an unexpected throw from
        // `applyEvent`): cancel the underlying body stream before releasing
        // the reader lock so the connection is torn down instead of left
        // half-read on the natural done-path.
        cancelReader();
        throw streamError;
      } finally {
        reader.releaseLock();
      }

      log.api.response('POST', `/api/conversations/${conversationId}/chat`, response.status, {
        messageLength: fullContent.length,
        contextCount: context.length,
        streaming: true
      });

      return {
        message: { role: 'assistant' as const, content: fullContent },
        context,
        id: Date.now().toString(),
        created: Math.floor(Date.now() / 1000)
      };
    },
    onMutate: async ({ conversationId, content }) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: conversationKeys.detail(conversationId)
      });

      // Snapshot previous value for rollback
      const previousConversation = queryClient.getQueryData(
        conversationKeys.detail(conversationId)
      );

      // Optimistically update cache with temporary user message
      queryClient.setQueryData(
        conversationKeys.detail(conversationId),
        (old: any) => {
          if (!old) {return old;}

          const optimisticMessage = {
            id: `temp-${Date.now()}`, // Temporary ID
            role: 'user',
            content,
            sources: [],
            timestamp: new Date().toISOString(),
            message_index: old.messages?.length || 0
          };

          return {
            ...old,
            messages: [...(old.messages || []), optimisticMessage]
          };
        }
      );

      return { previousConversation };
    },
    onSuccess: (data, { conversationId }) => {
      // Invalidate conversation details to refresh messages
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId)
      });

      // Also invalidate messages query
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.messages(conversationId)
      });

      // Update conversation list to reflect new message count and preview
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.lists()
      });

      log.user.action('message_sent', 'useConversationChat', {
        conversationId,
        hasContext: (data.context?.length ?? 0) > 0,
      });
    },
    onError: (error, { conversationId }, context) => {
      // Roll back the optimistic (temp-*) user message first, for instant
      // UI feedback -- but the backend persists the real user message
      // BEFORE generation starts, so a stream error (SseStreamError) or any
      // failure that occurs once the request reached the server can still
      // mean the user's message exists server-side (the assistant reply
      // itself is only persisted once the stream completes successfully, so
      // there is no partial assistant content to reconcile -- just the user
      // message). Restoring the pre-mutation snapshot alone would make that
      // message vanish from the cache. Invalidate afterward so the next
      // fetch reconciles against server state -- if nothing was persisted
      // (e.g. the request never reached the server) this just re-confirms
      // the rolled-back snapshot; if it was, the real message reappears.
      // This intentionally does not distinguish HTTP-failure (response
      // never ok) from stream-failure (error mid-SSE): both go through the
      // same rollback-then-reconcile path, since the query refetch is the
      // source of truth either way.
      if (context?.previousConversation) {
        queryClient.setQueryData(
          conversationKeys.detail(conversationId),
          context.previousConversation
        );
      }
      // Mirror the invalidation onSuccess performs: the detail query alone
      // won't refresh a separately-cached messages query, and the persisted
      // user message (see above) needs both to reconcile after rollback.
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.messages(conversationId),
      });
      log.api.error('POST', `/api/conversations/${conversationId}/chat`, error);
    },
  });
}